import { Map } from 'immutable';
import { Observable, Subscription } from 'rxjs';

import { AppRouter } from '@entities/AppRouter';
import { Behavior } from '@entities/Behavior';
import { Configuration } from '@entities/Configuration';
import { CustomWindow } from '@entities/CustomWindow';
import { TargetedDisplay } from '@entities/TargetedDisplay';
import { TransactionalService } from '@entities/TransactionalService';
import { TSVisibilityType } from '@entities/TSVisibilityType';

import { Logger } from './Logger';

declare let window: CustomWindow;

/**
 * Set Sentiments window information
 */
export function setSentimentsInfo(
  element: HTMLElement,
  version: string,
  build: string,
  environment: string,
  fn: Record<string, () => void>,
): void {
  window.wogaaCustom = {
    ...window.wogaaCustom,
    sentiments: {
      element,
      environment,
      version: {
        app: version,
        build,
      },
      ...fn,
    },
  };
  return;
}

/**
 * Creates an observable that emits samples from an easing function on every animation frame
 * for a duration `d` ms.
 *
 * The first value will be emitted on the next animation frame,
 * and is the value of the easing function at `t = 0`.
 * The final value is guaranteed to be the easing function at `t = d`.
 * The observable completes one frame after the final value was emitted.
 *
 * @param {function(t: number, b: number, c: number, d: number, [s]: number): number} easingFunction
 * - the easing function to sample from; can use any of Robert Penner's easing functions
 (without the `x` parameter)
 * @param {number} b - beginning value and 2nd parameter of the easing function
 * @param {number} c - change in value (or end value) and 3rd parameter of the easing function
 * @param {number} d - total duration of the tween in ms and 4th parameter of the easing function
 * @param {number} [s] - 5th parameter of the easing function (optional)
 * @return {Observable<number>} - an observable emitting samples of the easing function on
 *   animation frames for `d` ms.
 */
export function createTween(
  easingFunction: (t, b, c, d, s) => number,
  b: number,
  c: number,
  d: number,
  s?: number,
) {
  return new Observable(observer => {
    let startTime;
    let id = requestAnimationFrame(function sample(time) {
      startTime = startTime || time;
      const t = time - startTime;
      if (t < d) {
        observer.next(easingFunction(t, b, c, d, s));
        id = requestAnimationFrame(sample);
      } else {
        observer.next(easingFunction(d, b, c, d, s));
        id = requestAnimationFrame(function () {
          return observer.complete();
        });
      }
    });
    return () => {
      if (id) {
        cancelAnimationFrame(id);
      }
    };
  });
}

/**
 * TS Tracking Id list
 * @param transactionalServices list of transactional services
 */
export function transactionalServicesResolve(
  transactionalServices: TransactionalService[],
): Map<string, TransactionalService> {
  if (Array.isArray(transactionalServices)) {
    return Map(transactionalServices.map(t => [t.trackingId, t]));
  }
  return Map();
}

/**
 * Check property validator
 */
export function checkPropertyValidity(payload: object, props = []): boolean {
  for (const prop of props) {
    const value = payload[prop];
    if (value === null || value === undefined) {
      Logger.log(`Error: Event "${payload['event']}" required property: ${prop}`);
      return false;
    }
  }
  return true;
}

/**
 * Get router state for information service
 * @param isEnableForIs
 * @param isAppInBehavior
 * @param configuration
 */
export function getStateForInformationalService(
  isEnableForIs: boolean,
  isAppInBehavior: boolean,
  configuration: Configuration,
  brtEnabled: boolean,
): AppRouter {
  if (isEnableForIs) {
    if (isAppInBehavior) {
      if(brtEnabled) {
        return configuration.defaultState
      }
      return AppRouter.DisplayNoneRoute;
    }
    return configuration.defaultState;
  }

  return AppRouter.DisplayNoneRoute;
}

/**
 * Get router state for transactional service
 * @param ts
 * @param configuration
 * @param options
 */
export function getStateForTransactionalService(
  ts: TransactionalService,
  configuration: Configuration,
  options: {
    platform: TargetedDisplay;
    isInSession: boolean;
    isRatingSubmitted: boolean;
    isCompleted: boolean;
  },
  brtEnabled: boolean,
): AppRouter {
  // check for the platform availability
  if (!ts.isEnabled && !ts.targetedDisplays.includes(options.platform)) {
    return AppRouter.DisplayNoneRoute;
  }

  // Handle for always visible
  if (configuration.tsVisibilityType === TSVisibilityType.AlwaysVisible) {
    const states = {
      [Behavior.OverAndOverAgain]: () =>
        options.isCompleted ? brtEnabled ? configuration.defaultState : AppRouter.DisplayNoneRoute : configuration.defaultState,
      [Behavior.NeverAgainInSession]: () =>
        options.isInSession ? brtEnabled ? configuration.defaultState : AppRouter.DisplayNoneRoute : configuration.defaultState,
      [Behavior.NeverAgainInTransaction]: () =>
        options.isRatingSubmitted ? brtEnabled ? configuration.defaultState : AppRouter.DisplayNoneRoute : configuration.defaultState,
    };

    return states[ts.behavior]();
  }

  return brtEnabled ? configuration.defaultState : AppRouter.DisplayNoneRoute;
}

/**
 * Unsubscribe to all subscriptions
 * @param subscriptions
 */
export function unsubscribeAll(...subscriptions: Subscription[]) {
  subscriptions.forEach((sub: Subscription) => {
    if (sub) {
      sub.unsubscribe();
    }
  });
}
